import { __read } from "tslib";
import { runBeforeLayout } from '@office-start/performance-utility';
import { IDLE_VERYLOW_PRIORITY, scheduleWork, } from '@office-start/scheduler-service';
import { useEffect, useRef, useState } from 'react';
/**
 * Ensure that actions menus are not rendered until after work has been scheduled or user interaction.
 * This hook is currently used to control...
 *   action menu UX visibility - context menu, more (...) actions, and command menu (share)
 *   Document action creation - Since actions are behind user interaction they do not need to be created before first paint
 *
 * @param componentRef Component that allows interactivity to trigger a context, command or action menu
 * @returns false initially, but true after setActionsMenuRenderingPhase has been called during idle time or
 *          when user interacts with the component on mouse over or context menu click
 */
export function useActionsMenuRenderer(componentRef) {
    var _a = __read(useState(false), 2), actionsMenuRenderingPhase = _a[0], setActionsMenuRenderingPhase = _a[1];
    var workHandle = useRef(null);
    // Schedule 'setActionsMenuRenderingPhase' during idle at low priority
    useEffect(function () {
        workHandle.current = scheduleWork(function () {
            return runBeforeLayout(function () {
                setActionsMenuRenderingPhase(true);
            });
        }, IDLE_VERYLOW_PRIORITY);
        return function () {
            var _a;
            (_a = workHandle.current) === null || _a === void 0 ? void 0 : _a.cancel();
        };
    }, []);
    // On user interactivity for context menu event setActionsMenuRenderingPhase
    useEffect(function () {
        var contextMenuListener = function (e) {
            var _a;
            if (componentRef.current &&
                e.target instanceof Node &&
                componentRef.current.contains(e.target) &&
                e.type === 'contextmenu') {
                if (!actionsMenuRenderingPhase) {
                    e.preventDefault();
                    (_a = workHandle.current) === null || _a === void 0 ? void 0 : _a.cancelAndRunNow();
                    setActionsMenuRenderingPhase(true);
                }
                // Run at most once
                document.removeEventListener('contextmenu', contextMenuListener);
            }
        };
        document.addEventListener('contextmenu', contextMenuListener);
        return function () {
            document.removeEventListener('contextmenu', contextMenuListener);
        };
    }, [componentRef, actionsMenuRenderingPhase]);
    // If user mouses over componentRef and we have yet to set rendering phase, set it
    // Remove the listener when rendering phase is true...
    useEffect(function () {
        var _a;
        var onMouseOver = function (_e) {
            var _a, _b;
            if (!actionsMenuRenderingPhase) {
                (_a = workHandle.current) === null || _a === void 0 ? void 0 : _a.cancelAndRunNow();
                setActionsMenuRenderingPhase(true);
            }
            // Run at most once
            (_b = componentRef.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('mouseover', onMouseOver);
        };
        (_a = componentRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('mouseover', onMouseOver);
        return function () {
            var _a;
            (_a = componentRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('mouseover', onMouseOver);
        };
    }, [componentRef, actionsMenuRenderingPhase]);
    return actionsMenuRenderingPhase;
}
//# sourceMappingURL=useActionsMenuRenderer.js.map